import { useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

const SEOEnhancements = () => {
  const { language } = useLanguage();

  useEffect(() => {
    // Update HTML lang attribute for better SEO
    document.documentElement.lang = language === 'ar' ? 'ar' : 'en';
    
    // Add hreflang meta tags dynamically
    const existingHreflang = document.querySelectorAll('link[hreflang]');
    existingHreflang.forEach(link => link.remove());

    const hreflangs = [
      { lang: 'en', href: 'https://arabianalwaha.com/' },
      { lang: 'ar', href: 'https://arabianalwaha.com/?lang=ar' },
      { lang: 'x-default', href: 'https://arabianalwaha.com/' }
    ];

    hreflangs.forEach(({ lang, href }) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = lang;
      link.href = href;
      document.head.appendChild(link);
    });

    // Enhanced FAQs with Arabic and local Saudi Arabia focus
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
      "name": "ما هي الخدمات التي تقدمها مؤسسة العربية الواحة؟ What services does Arabian Alwaha provide?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "مؤسسة العربية الواحة (Arabian Alwaha Est) هي المؤسسة الرائدة في المقاولات والتجارة في الجبيل بالمملكة العربية السعودية، تقدم خدمات شاملة في المقاولات العامة، تجارة المواد، توريد المعدات، حلول القوى العاملة، وخدمات تأجير المركبات في جميع أنحاء السعودية ودول مجلس التعاون من موقعنا الاستراتيجي في مدينة الجبيل الصناعية. Arabian Alwaha Establishment is the leading contracting and trading establishment in Jubail, Saudi Arabia providing comprehensive general contracting, materials trading, equipment supply, workforce solutions, and vehicle rental across Saudi Arabia and GCC from Jubail Industrial City."
          }
        },
        {
          "@type": "Question", 
          "name": "أين تقع مؤسسة العربية الواحة وكيفية التواصل؟ Where is Arabian Alwaha located?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "تقع مؤسسة العربية الواحة في موقع استراتيجي بمدينة الجبيل الصناعية، المنطقة الشرقية، المملكة العربية السعودية. يمكنكم التواصل مع مؤسسة العربية الواحة على البريد الإلكتروني sales@arabianalwaha.com أو زيارة مكتبنا في مدينة الجبيل الصناعية لجميع احتياجاتكم من المقاولات والتجارة. Arabian Alwaha Establishment is strategically located in Jubail Industrial City, Eastern Province, Saudi Arabia. Contact us at sales@arabianalwaha.com."
          }
        },
        {
          "@type": "Question",
      "name": "لماذا تختار مؤسسة العربية الواحة لخدمات المقاولات والتجارة؟ Why choose Arabian Alwaha?",
          "acceptedAnswer": {
            "@type": "Answer", 
            "text": "مؤسسة العربية الواحة هي المؤسسة الرائدة في المقاولات والتجارة في الجبيل بالمملكة العربية السعودية منذ عام 2008، بخبرة تزيد عن 15 عاماً. تثق بنا كبرى الشركات في المقاولات العامة وتجارة المواد والحلول الشاملة في الجبيل وجميع أنحاء السعودية. Arabian Alwaha Est has been the leading contracting and trading establishment in Jubail since 2008 with 15+ years expertise, trusted by major companies across Saudi Arabia."
          }
        },
        {
          "@type": "Question",
      "name": "ما الذي يميز مؤسسة العربية الواحة عن شركات المقاولات الأخرى؟ What makes Arabian Alwaha different?",
          "acceptedAnswer": {
            "@type": "Answer", 
            "text": "تتميز مؤسسة العربية الواحة كمؤسسة مقاولات وتجارة رائدة في الجبيل بالخبرة المحلية العميقة، الموقع الاستراتيجي في مدينة الجبيل الصناعية، محفظة الخدمات الشاملة، وسجل حافل منذ 2008. تقدم مؤسسة العربية الواحة حلولاً متكاملة من المقاولات إلى التجارة والقوى العاملة، مما يجعلنا الشريك المفضل لاحتياجات المشاريع في السعودية. Arabian Alwaha Est stands out as leading contracting and trading establishment in Jubail with deep local Saudi expertise, strategic location, comprehensive services, and proven track record since 2008."
          }
        },
        {
          "@type": "Question",
          "name": "هل مؤسسة العربية الواحة مرخصة في السعودية؟ Is Arabian Alwaha licensed in Saudi Arabia?",
          "acceptedAnswer": {
            "@type": "Answer", 
            "text": "نعم، مؤسسة العربية الواحة مرخصة بالكامل ومسجلة رسمياً في المملكة العربية السعودية. نحن عضو في الغرفة التجارية ولدينا جميع التراخيص والشهادات المطلوبة لممارسة أعمال المقاولات والتجارة في السعودية ودول مجلس التعاون الخليجي. Yes, Arabian Alwaha Establishment is fully licensed and officially registered in Saudi Arabia with all required certifications for contracting and trading operations in KSA and GCC."
          }
        }
      ]
    };

    // Add Local Business Schema for Saudi Arabia
    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": "https://arabianalwaha.com/#localbusiness",
      "name": "مؤسسة العربية الواحة - Arabian Alwaha Establishment",
      "alternateName": ["Arabian Alwaha Est", "مؤسسة العربية الواحة", "العربية الواحة"],
      "description": "المؤسسة الرائدة في المقاولات والتجارة في الجبيل بالمملكة العربية السعودية - Leading contracting and trading establishment in Jubail, Saudi Arabia",
      "url": "https://arabianalwaha.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://arabianalwaha.com/assets/arabian-alwaha-logo.jpg",
        "width": 300,
        "height": 100
      },
      "image": "https://arabianalwaha.com/assets/arabian-alwaha-logo.jpg",
      "telephone": "+966-13-XXX-XXXX",
      "email": "sales@arabianalwaha.com",
      "foundingDate": "2008",
      "numberOfEmployees": "50-100",
      "currenciesAccepted": ["SAR", "USD", "EUR"],
      "paymentAccepted": ["Cash", "Credit Card", "Bank Transfer"],
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Jubail Industrial City",
        "addressLocality": "Jubail",
        "addressRegion": "Eastern Province", 
        "postalCode": "31951",
        "addressCountry": "SA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 27.0174,
        "longitude": 49.6593
      },
      "areaServed": [
        {
          "@type": "Country",
          "name": "Saudi Arabia"
        },
        {
          "@type": "AdministrativeArea", 
          "name": "Eastern Province"
        },
        {
          "@type": "City",
          "name": "Jubail"
        }
      ],
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": 27.0174,
          "longitude": 49.6593
        },
        "geoRadius": "500000"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Contracting and Trading Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "المقاولات العامة - General Contracting",
              "description": "خدمات المقاولات العامة الشاملة في السعودية"
            }
          },
          {
            "@type": "Offer", 
            "itemOffered": {
              "@type": "Service",
              "name": "تجارة المواد - Materials Trading",
              "description": "خدمات تجارة مواد البناء والإمدادات الصناعية"
            }
          }
        ]
      },
      "makesOffer": [
        {
          "@type": "Offer",
          "name": "General Contracting Services",
          "description": "Comprehensive contracting services across Saudi Arabia and GCC"
        },
        {
          "@type": "Offer", 
          "name": "Materials and Equipment Supply",
          "description": "Supply of construction materials and industrial equipment"
        }
      ]
    };

    // Remove existing schemas if any
    const existingFAQ = document.querySelector('script[type="application/ld+json"][data-faq]');
    if (existingFAQ) existingFAQ.remove();
    
    const existingLocal = document.querySelector('script[type="application/ld+json"][data-local]');
    if (existingLocal) existingLocal.remove();

    // Add FAQ schema
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.setAttribute('data-faq', 'true');
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    // Add Local Business schema for Saudi Arabia
    const localScript = document.createElement('script');
    localScript.type = 'application/ld+json';
    localScript.setAttribute('data-local', 'true');
    localScript.textContent = JSON.stringify(localBusinessSchema);
    document.head.appendChild(localScript);

    // Add additional meta tags for Saudi Arabia targeting
    const saudiMeta = [
      { name: 'geo.country', content: 'SA' },
      { name: 'geo.region', content: 'Eastern Province' },
      { name: 'geo.city', content: 'Jubail' },
      { name: 'business.region', content: 'Saudi Arabia' },
      { name: 'business.country_code', content: 'SA' },
      { name: 'location', content: 'Jubail Industrial City, Saudi Arabia' }
    ];

    saudiMeta.forEach(meta => {
      const existing = document.querySelector(`meta[name="${meta.name}"]`);
      if (!existing) {
        const metaTag = document.createElement('meta');
        metaTag.name = meta.name;
        metaTag.content = meta.content;
        document.head.appendChild(metaTag);
      }
    });

  }, [language]);

  return null; // This component doesn't render anything
};

export default SEOEnhancements;